import React from 'react';
import { Box, Typography, Tooltip, Button } from "@mui/material";
import { styled } from '@mui/material/styles';
import { tooltipClasses } from '@mui/material/Tooltip';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { colors } from '../../utils/themeUtils';

// Custom styled tooltip to match the app's tooltip style
const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 'none',
    whiteSpace: 'nowrap',
    backgroundColor: 'rgba(250, 250, 252, 0.98)',
    padding: '10px 14px',
    fontSize: '0.85rem',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    color: '#333',
    '&.MuiTooltip-tooltipPlacementTop': {
      marginBottom: '6px'
    }
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: 'rgba(250, 250, 252, 0.98)'
  }
});

/**
 * Component to render a single subject action item in the Recommended Actions section
 */
const SubjectActionItem = ({
  subject,
  attributes,
  view,
  isSubjectHighlighted,
  isAttributeHighlightingParent,
  handleSubjectClick,
  handleAttributeClick,
  formatCurrency,
  formatImpact,
  getSubjectImpactAction,
  hideIcon,
  insightsCount
}) => {
  // Defensively check if subject and its core properties are valid
  if (
    !subject || 
    typeof subject.name !== 'string' || 
    typeof subject.isPositive !== 'boolean' ||
    typeof subject.impact !== 'number' ||       // Check for impact
    typeof subject.averageRating !== 'number' || // Check for averageRating
    typeof subject.revenueImpact !== 'number'   // Check for revenueImpact
  ) {
    console.error("SubjectActionItem: Invalid or incomplete subject prop", subject);
    return null; 
  }

  // There should always be exactly one attribute
  const attribute = attributes && attributes.length > 0 ? attributes[0] : null;
  if (!attribute) {
    console.error("SubjectActionItem: No attribute provided for subject-attribute pair", subject);
    return null;
  }
  
  // Helper function to get impact text with colored styling
  const getImpactElement = (isPositive, impact, revenueImpact) => {
    const value = view === "Sales" ? revenueImpact : impact;
    const formattedValue = view === "Sales" 
      ? formatCurrency(Math.abs(value || 0))
      : formatImpact(Math.abs(value || 0));
    return (
      <span style={{ 
        color: isPositive ? colors.positiveGreen : colors.negativeRed,
        fontWeight: 600
      }}>
        {isPositive ? '+' : '-'}{formattedValue}
      </span>
    );
  };

  // Tooltip content for the whole row
  const tooltipContent = getImpactElement(
    attribute.sentiment === 'positive',
    view === "Rating" ? attribute.averageRating : attribute.impact,
    attribute.revenueImpact
  );

  const isActive = isAttributeHighlightingParent(subject.name, attribute.name);

  return (
    <Box 
      data-action-box="true"
      data-filter-exempt="true"
      sx={{ 
        padding: '0rem 0rem 0rem 0rem',
        mb: 0.5,
        display: 'flex',
        width: '100%',
        alignItems: 'center',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
        {!hideIcon && (
          <Box sx={{ mr: 0.5, mt: 0.5, flexShrink: 0 }} data-filter-exempt="true">
            {subject.isPositive ? (
              <CheckCircleOutlineIcon sx={{ color: colors.positiveGreen, fontSize: '1.25rem' }} data-filter-exempt="true" />
            ) : (
              <ErrorOutlineIcon sx={{ color: colors.negativeRed, fontSize: '1.25rem' }} data-filter-exempt="true" />
            )}
          </Box>
        )}
        <StyledTooltip
          title={tooltipContent}
          arrow
          placement="top"
        >
          <Button
            variant="outlined"
            onClick={() => handleAttributeClick(attribute.name, subject.name)}
            sx={{
              py: 0.5,
              px: 1.5,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              textTransform: 'none',
              backgroundColor: isActive ? 'rgba(25, 118, 210, 0.10)' : 'transparent',
              transition: 'background 0.15s',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.05)',
                textDecoration: 'underline',
              },
              textDecoration: isActive ? 'underline' : 'none',
              minWidth: 0,
              width: 'fit-content',
              lineHeight: 1.7,
              marginBottom: '6px',
            }}
            data-filter-exempt="true"
          >
            <Typography 
              component="span" 
              variant="body1" 
              sx={{ 
                lineHeight: 1.7, 
                color: 'inherit', 
                fontWeight: 400,
                textAlign: 'left',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }} 
              data-filter-exempt="true"
            >
              <span style={{
                color: subject.isPositive ? 'rgba(46, 125, 50, 0.9)' : 'rgba(211, 47, 47, 0.9)',
                backgroundColor: subject.isPositive ? 'rgba(46, 125, 50, 0.08)' : 'rgba(211, 47, 47, 0.08)',
                fontWeight: 500,
                padding: '2px 6px',
                borderRadius: '4px',
                marginRight: '8px'
              }}>
                {subject.isPositive ? 'Continue' : 'Improve'}
              </span>
              <span> </span>
              <span style={{ color: subject.isPositive ? colors.positiveGreen : colors.negativeRed, fontWeight: 400 }}>
                {subject.name}
              </span>
              <span> </span>
              <span style={{ color: attribute.sentiment === 'positive' ? colors.positiveGreen : colors.negativeRed, fontWeight: 400 }}>
                {attribute.name}
              </span>
            </Typography>
            {typeof insightsCount === 'number' && insightsCount > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', ml: 1 }}>
                <VisibilityIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                <Typography variant="caption" sx={{ fontWeight: 500, lineHeight: 1.5, color: 'inherit' }}>
                  {insightsCount}
                </Typography>
              </Box>
            )}
          </Button>
        </StyledTooltip>
      </Box>
    </Box>
  );
};

export default SubjectActionItem; 